import { Loader2 } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ProfileData } from "@/types/supabase";

import UserRow from "./UserRow";

interface UserData extends ProfileData {
  isAdmin: boolean;
  isActive: boolean;
}

interface UserManagementTableProps {
  users: UserData[];
  onRoleChange: (userId: string, makeAdmin: boolean) => Promise<void>;
  onStatusChange: (userId: string, makeActive: boolean) => Promise<void>;
  onDeleteUser: (userId: string) => Promise<void>;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  summary: string;
}

const UserManagementTable = ({
  users,
  onRoleChange,
  onStatusChange,
  onDeleteUser,
  isLoading,
  isError,
  onRetry,
  summary,
}: UserManagementTableProps) => {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-900">User directory</h2>
        <p className="mt-1 text-sm text-gray-500">{summary}</p>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="border-gray-200 hover:bg-transparent">
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">User</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Email</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Status</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Role</TableHead>
            <TableHead className="bg-white text-right text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                <div className="flex items-center justify-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading users
                </div>
              </TableCell>
            </TableRow>
          ) : isError ? (
            <TableRow>
              <TableCell colSpan={5} className="h-28 text-center">
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-900">User data could not be loaded.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
                    onClick={onRetry}
                  >
                    Retry
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ) : users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="h-28 text-center">
                <p className="text-sm text-gray-500">No users match the current filters.</p>
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                onRoleChange={onRoleChange}
                onStatusChange={onStatusChange}
                onDeleteUser={onDeleteUser}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default UserManagementTable;
